import { ValueOf } from '@shared/types/common.ts';
import { UserCountryCode, UserCountryCodes } from '@shared/types/countryCodes.ts';

const DEFAULT_REQUIRED_DISPLAY_CREATIVES_COUNT = 2;

export const DisplayCreativesCountModes = {
  L_SHAPE_ONLY: 'lShapeOnly',
  L_SHAPE_AND_LANDING: 'lShapeAndLanding',
  SWITCH_IN_LEFT_AND_BOTTOM: 'switchInLeftAndBottom',
} as const;

export type DisplayCreativesCountMode = ValueOf<typeof DisplayCreativesCountModes>;

export const AdProductsNotEnoughCreativesInfoTranslationKeys = {
  DISPLAY: 'builder.steps.step03.notEnoughCreativesInfo.display',
  DISPLAY_SINGLE_L_SHAPE: 'builder.steps.step03.notEnoughCreativesInfo.displaySingleLShape',
  DISPLAY_SWITCH_IN: 'builder.steps.step03.notEnoughCreativesInfo.displaySwitchIn',
} as const;

export type AdProductsNotEnoughCreativesInfoTranslationKey = ValueOf<
  typeof AdProductsNotEnoughCreativesInfoTranslationKeys
>;

export const AdProductsReviewUploadedCreativesLabelKeys = {
  DEFAULT: 'review.labels.uploadedCreatives',
  SPAIN: 'review.labels.uploadedCreativesES',
  GERMANY: 'review.labels.uploadedCreativesDE',
} as const;

export type AdProductsReviewUploadedCreativesLabelKey = ValueOf<typeof AdProductsReviewUploadedCreativesLabelKeys>;

export const AdProductsReviewNotEnoughCreativesInfoTranslationKeys = {
  DEFAULT: 'builder.steps.step05.notEnoughCreativesInfo.content',
  SPAIN: 'builder.steps.step05.notEnoughCreativesInfo.contentSingleLShape',
  GERMANY: 'builder.steps.step05.notEnoughCreativesInfo.contentSwitchIn',
} as const;

export type AdProductsReviewNotEnoughCreativesInfoTranslationKey = ValueOf<
  typeof AdProductsReviewNotEnoughCreativesInfoTranslationKeys
>;

export interface CampaignAdProductsCountryConfig {
  requiredDisplayCreativesCount: number;
  displayCreativesCountMode: DisplayCreativesCountMode;
  isVideoAdProductAvailable: boolean;
  isLShapeCreativeAvailable: boolean;
  isLandingCreativeAvailable: boolean;
  isSwitchInCreativeAvailable: boolean;
  forcedFormatTypes: { display: boolean; video: boolean } | null;
  isDisplayFormatTypeLockedByCountry: boolean;
  notEnoughCreativesInfoTranslationKey: AdProductsNotEnoughCreativesInfoTranslationKey;
  reviewUploadedCreativesLabelKey: AdProductsReviewUploadedCreativesLabelKey;
  reviewNotEnoughCreativesInfoTranslationKey: AdProductsReviewNotEnoughCreativesInfoTranslationKey;
}

export const DEFAULT_CAMPAIGN_AD_PRODUCTS_COUNTRY_CONFIG: CampaignAdProductsCountryConfig = {
  requiredDisplayCreativesCount: DEFAULT_REQUIRED_DISPLAY_CREATIVES_COUNT,
  displayCreativesCountMode: DisplayCreativesCountModes.L_SHAPE_AND_LANDING,
  isVideoAdProductAvailable: true,
  isLShapeCreativeAvailable: true,
  isLandingCreativeAvailable: true,
  isSwitchInCreativeAvailable: false,
  forcedFormatTypes: null,
  isDisplayFormatTypeLockedByCountry: false,
  notEnoughCreativesInfoTranslationKey: AdProductsNotEnoughCreativesInfoTranslationKeys.DISPLAY,
  reviewUploadedCreativesLabelKey: AdProductsReviewUploadedCreativesLabelKeys.DEFAULT,
  reviewNotEnoughCreativesInfoTranslationKey: AdProductsReviewNotEnoughCreativesInfoTranslationKeys.DEFAULT,
};

export const SPAIN_CAMPAIGN_AD_PRODUCTS_COUNTRY_CONFIG: CampaignAdProductsCountryConfig = {
  requiredDisplayCreativesCount: 1,
  displayCreativesCountMode: DisplayCreativesCountModes.L_SHAPE_ONLY,
  isVideoAdProductAvailable: false,
  isLShapeCreativeAvailable: true,
  isLandingCreativeAvailable: false,
  isSwitchInCreativeAvailable: false,
  forcedFormatTypes: { display: true, video: false },
  isDisplayFormatTypeLockedByCountry: true,
  notEnoughCreativesInfoTranslationKey: AdProductsNotEnoughCreativesInfoTranslationKeys.DISPLAY_SINGLE_L_SHAPE,
  reviewUploadedCreativesLabelKey: AdProductsReviewUploadedCreativesLabelKeys.SPAIN,
  reviewNotEnoughCreativesInfoTranslationKey: AdProductsReviewNotEnoughCreativesInfoTranslationKeys.SPAIN,
};

export const GERMANY_CAMPAIGN_AD_PRODUCTS_COUNTRY_CONFIG: CampaignAdProductsCountryConfig = {
  requiredDisplayCreativesCount: DEFAULT_REQUIRED_DISPLAY_CREATIVES_COUNT,
  displayCreativesCountMode: DisplayCreativesCountModes.SWITCH_IN_LEFT_AND_BOTTOM,
  isVideoAdProductAvailable: false,
  isLShapeCreativeAvailable: false,
  isLandingCreativeAvailable: false,
  isSwitchInCreativeAvailable: true,
  forcedFormatTypes: null,
  isDisplayFormatTypeLockedByCountry: true,
  notEnoughCreativesInfoTranslationKey: AdProductsNotEnoughCreativesInfoTranslationKeys.DISPLAY_SWITCH_IN,
  reviewUploadedCreativesLabelKey: AdProductsReviewUploadedCreativesLabelKeys.GERMANY,
  reviewNotEnoughCreativesInfoTranslationKey: AdProductsReviewNotEnoughCreativesInfoTranslationKeys.GERMANY,
};

const CAMPAIGN_AD_PRODUCTS_COUNTRY_CONFIG: Record<UserCountryCode, CampaignAdProductsCountryConfig> = {
  [UserCountryCodes.IT]: DEFAULT_CAMPAIGN_AD_PRODUCTS_COUNTRY_CONFIG,
  [UserCountryCodes.DE]: GERMANY_CAMPAIGN_AD_PRODUCTS_COUNTRY_CONFIG,
  [UserCountryCodes.ES]: SPAIN_CAMPAIGN_AD_PRODUCTS_COUNTRY_CONFIG,
};

export function getCampaignAdProductsCountryConfig(country: UserCountryCode): CampaignAdProductsCountryConfig {
  return CAMPAIGN_AD_PRODUCTS_COUNTRY_CONFIG[country];
}
